import { useEffect, useState } from "react";

import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { PrcDialogContent } from "./PrcDialogContent";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Textarea } from "../../ui/textarea";
import { cn } from "../../utils/common/cn";
import {
  RESIDUAL_TYPES,
  useResidualCodeProposals,
  type ResidualCode,
  type ResidualCodeProposal,
  type ResidualType,
} from "../../utils/portfolio-risk-concentration/api/residualCodes";

const PAGE_SIZE = 10;

/**
 * A residual code. Codes are stored trimmed, but one that still has surrounding whitespace (a legacy
 * value) would look identical to its trimmed twin, so it is shown in quotes with the whitespace kept.
 */
export function ResidualCodeText({ code }: { code: string }) {
  const padded = code !== code.trim();
  return (
    <span
      className="whitespace-pre font-mono"
      title={padded ? "This code has a leading or trailing space" : undefined}
    >
      {padded ? `"${code}"` : code}
    </span>
  );
}

/** "Not set → Loans", "Loans → Leases": how a proposal's change reads in the queue and the log. */
export function residualChangeSummary(p: ResidualCodeProposal): string {
  if (p.changeType === "ADD") return `New code → ${p.newResidualType || "—"}`;
  return `${p.oldResidualType || "Not set"} → ${p.newResidualType || "—"}`;
}

/**
 * Propose a Loans / Leases tag for one residual code.
 *
 * Nothing changes until a contributor approves it. The reason is mandatory, as on every proposal in
 * this product, and the new type must differ from what the row reads now.
 */
export function ResidualCodeDialog({
  open,
  onOpenChange,
  row,
  propose,
  onSubmitted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: ResidualCode | null;
  propose: (input: {
    residualCode: string;
    residualType: ResidualType;
    reason: string;
    changeType?: "ADD" | "ADJUST";
  }) => Promise<unknown>;
  onSubmitted?: (message: string) => void;
}) {
  const [type, setType] = useState<ResidualType | "">("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType("");
    setReason("");
    setError(null);
  }, [open, row?.residualCode]);

  async function submit() {
    if (!row) return;
    if (!type) {
      setError("Choose Loans or Leases.");
      return;
    }
    if (row.residualType === type) {
      setError(`This residual code is already ${type}.`);
      return;
    }
    if (!reason.trim()) {
      setError("A reason is required for every proposal.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await propose({
        residualCode: row.residualCode,
        residualType: type,
        reason: reason.trim(),
        changeType: "ADJUST",
      });
      onOpenChange(false);
      onSubmitted?.(
        `Change proposed for residual code ${row.residualCode.trim()} — awaiting review.`,
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "The proposal could not be raised.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PrcDialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Propose residual type change</DialogTitle>
          <DialogDescription>
            {row ? (
              <>
                Residual code <strong><ResidualCodeText code={row.residualCode} /></strong> is
                currently <strong>{row.residualType || "not set"}</strong>. Nothing changes until
                this is approved.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          <div>
            <Label>New residual type</Label>
            <Select value={type || undefined} onValueChange={(v) => setType(v as ResidualType)}>
              <SelectTrigger className="mt-1" aria-label="New residual type">
                <SelectValue placeholder="Choose Loans or Leases" />
              </SelectTrigger>
              <SelectContent>
                {RESIDUAL_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="rc-reason">Reason</Label>
            <Textarea
              id="rc-reason"
              rows={3}
              placeholder="Why does this residual code mark a loan or a lease?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={submitting} onClick={submit}>
            {submitting ? "Submitting…" : "Propose change"}
          </Button>
        </DialogFooter>
      </PrcDialogContent>
    </Dialog>
  );
}

/**
 * Propose a NEW residual code with its Loans / Leases type. It goes through the same queue as a change to
 * an existing code: nothing is added to the mapping until a contributor approves it.
 *
 * Spaces before or after the code are removed, here and again on the server.
 */
export function AddResidualCodeDialog({
  open,
  onOpenChange,
  propose,
  onSubmitted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propose: (input: {
    residualCode: string;
    residualType: ResidualType;
    reason: string;
    changeType?: "ADD" | "ADJUST";
  }) => Promise<unknown>;
  onSubmitted?: (message: string) => void;
}) {
  const [code, setCode] = useState("");
  const [type, setType] = useState<ResidualType | "">("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode("");
    setType("");
    setReason("");
    setError(null);
  }, [open]);

  async function submit() {
    const cleaned = code.trim();
    if (!cleaned) {
      setError("Enter the residual code.");
      return;
    }
    if (!type) {
      setError("Choose Loans or Leases.");
      return;
    }
    if (!reason.trim()) {
      setError("A reason is required for every proposal.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await propose({ residualCode: cleaned, residualType: type, reason: reason.trim(), changeType: "ADD" });
      onOpenChange(false);
      onSubmitted?.(`Residual code ${cleaned} proposed — awaiting review.`);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "The proposal could not be raised.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PrcDialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add residual code</DialogTitle>
          <DialogDescription>
            Proposes a residual code that is not in the mapping yet, and whether it marks a Loan or a
            Lease. Nothing is added until this is approved. Spaces before or after the code are
            removed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="rc-new-code">Residual code</Label>
            <Input
              id="rc-new-code"
              placeholder="e.g. FV"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="mt-1 font-mono"
            />
          </div>

          <div>
            <Label>Loan or Lease</Label>
            <Select value={type || undefined} onValueChange={(v) => setType(v as ResidualType)}>
              <SelectTrigger className="mt-1" aria-label="Loan or Lease">
                <SelectValue placeholder="Choose Loans or Leases" />
              </SelectTrigger>
              <SelectContent>
                {RESIDUAL_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="rc-new-reason">Reason</Label>
            <Textarea
              id="rc-new-reason"
              rows={3}
              placeholder="Where does this residual code come from, and why is it a loan or a lease?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={submitting} onClick={submit}>
            {submitting ? "Submitting…" : "Propose residual code"}
          </Button>
        </DialogFooter>
      </PrcDialogContent>
    </Dialog>
  );
}

/**
 * Pending residual-code proposals. Contributors and above can approve or reject (review notes
 * required); a reviewer sees the queue — they raised what is in it — but cannot decide.
 */
export function ResidualCodeApprovalQueue({
  proposals,
  onReview,
  canDecide,
}: {
  proposals: ResidualCodeProposal[];
  onReview: (
    p: ResidualCodeProposal,
    decision: "APPROVED" | "REJECTED",
    notes: string,
  ) => Promise<unknown>;
  canDecide: boolean;
}) {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  async function act(p: ResidualCodeProposal, decision: "APPROVED" | "REJECTED") {
    const note = (notes[p.proposalId] ?? "").trim();
    if (!note) {
      setErrors((e) => ({ ...e, [p.proposalId]: "Review notes are required." }));
      return;
    }
    try {
      setBusy(p.proposalId);
      await onReview(p, decision, note);
      setNotes((n) => ({ ...n, [p.proposalId]: "" }));
      setErrors((e) => ({ ...e, [p.proposalId]: "" }));
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [p.proposalId]: err instanceof Error ? err.message : "Review failed.",
      }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">
          Residual code approval queue{proposals.length ? ` (${proposals.length})` : ""}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {proposals.length === 0 ? (
          <p className="text-sm text-muted-foreground">No proposals awaiting review.</p>
        ) : (
          <div className="space-y-4">
            {proposals.map((p) => (
              <div key={p.proposalId} className="rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 text-sm">
                    <p className="font-medium">
                      {p.changeType === "ADD" ? "Add residual code" : "Residual code"}{" "}
                      <ResidualCodeText code={p.residualCode} />
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {residualChangeSummary(p)} · proposed by {p.proposedByRole}
                    </p>
                    {p.reason && <p className="mt-1 text-xs">{p.reason}</p>}
                  </div>
                  <Badge
                    variant="outline"
                    className="border-primary/50 text-[10px] uppercase tracking-[0.1em] text-primary"
                  >
                    Pending
                  </Badge>
                </div>

                {canDecide && (
                  <div className="mt-3 space-y-2">
                    <Textarea
                      rows={2}
                      placeholder="Review notes (required)"
                      value={notes[p.proposalId] ?? ""}
                      onChange={(e) => setNotes((n) => ({ ...n, [p.proposalId]: e.target.value }))}
                    />
                    {errors[p.proposalId] && (
                      <p className="text-xs text-destructive">{errors[p.proposalId]}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={busy === p.proposalId}
                        onClick={() => act(p, "APPROVED")}
                      >
                        {busy === p.proposalId ? "Applying…" : "Approve"}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={busy === p.proposalId}
                        onClick={() => act(p, "REJECTED")}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** The permanent change log — every proposal, decided or not, newest first, server-paged. */
export function ResidualCodeLog() {
  const [offset, setOffset] = useState(0);
  const logQ = useResidualCodeProposals({ limit: PAGE_SIZE, offset });
  const total = logQ.data?.total ?? 0;
  const page = logQ.data?.proposals ?? [];

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">Residual code change log</CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Residual code</TableHead>
              <TableHead>Change</TableHead>
              <TableHead>Proposed by</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Reviewed by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {total === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                  {logQ.isLoading ? "Loading…" : "Nothing proposed yet."}
                </TableCell>
              </TableRow>
            )}
            {page.map((p) => (
              <TableRow key={p.proposalId}>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[10px] uppercase tracking-[0.1em]",
                      p.status === "APPROVED"
                        ? "border-[color:var(--approve)]/50 text-[color:var(--approve)]"
                        : p.status === "REJECTED"
                          ? "border-destructive/50 text-destructive"
                          : "border-primary/50 text-primary",
                    )}
                  >
                    {p.status === "APPROVED" ? "Applied" : p.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <ResidualCodeText code={p.residualCode} />
                </TableCell>
                <TableCell className="whitespace-nowrap">{residualChangeSummary(p)}</TableCell>
                <TableCell className="text-muted-foreground">{p.proposedByRole}</TableCell>
                <TableCell className="max-w-[220px] text-xs">{p.reason}</TableCell>
                <TableCell className="max-w-[220px] text-xs text-muted-foreground">
                  {p.reviewedByRole ? (
                    <>
                      {p.reviewedByRole}
                      {p.reviewNotes && <div className="text-[11px]">{p.reviewNotes}</div>}
                    </>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between px-4 pt-3 text-xs text-muted-foreground">
          <span>
            {total === 0
              ? "0 of 0"
              : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
